/**
 * Main layout component
 *
 * Copyright (C) 2020A Noom, Inc.
 * @author nikola
 */

import React, { useEffect } from "react";
import { Box } from "@noom/wax-component-library";
import { useLogger } from "@noom/hive-analytics";

import {
  MIN_SCREEN_WIDTH,
  useDesktopView,
} from "../../../hooks/useDesktopView";
import { useAuthCredentials } from "../../../hooks/useAuthCredentials";
import { usePatient } from "../../../hooks/usePatient";

import NavBar from "./NavBar";

const MainLayout: React.FC<{
  showHeader?: boolean;
  showFooter?: boolean;
  children?: React.ReactNode;
}> = ({ children, showHeader = true, showFooter = true }) => {
  const desktopViewActive = useDesktopView();
  const patient = usePatient();
  const { accessCode } = useAuthCredentials();
  const mixpanelLogger = useLogger();

  useEffect(() => {
    if (patient?.user_id) {
      // Identity (distinct_id) is `email`, set at login in `onLoginSuccess` and
      // persisted by mixpanel across reloads — we do NOT re-identify here. We
      // only register `user_id` (and re-assert `access_code`) as super-properties
      // so events carry them as fields. Runs on session restore too, where the
      // profile resolves after `onLoginSuccess` would have fired.
      mixpanelLogger.register({
        user_id: patient.user_id,
        ...(accessCode ? { access_code: accessCode } : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient, accessCode]);

  return (
    <Box h="100%" display="flex" flexDir="column">
      <header>{showHeader && <NavBar />}</header>

      <Box
        as="main"
        maxW={desktopViewActive ? "container.xl" : "100vw"}
        m="0 auto"
        flexGrow={1}
        width={desktopViewActive ? `${MIN_SCREEN_WIDTH}px` : "100%"}
        margin="0 auto;"
      >
        {children}
      </Box>

      {showFooter && (
        <Box
          as="footer"
          textAlign="center"
          p={4}
          mt="auto"
          width={desktopViewActive ? "400px" : "100%"}
          margin="0 auto"
          color="gray"
          borderTop="1px solid gray"
        >
          Note: this page is only for viewing your Noom Telehealth Program
          details, no other use of this page is authorized. To access all your
          Noom features, download the Noom app to your phone and login there.
          {/* This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply. */}
        </Box>
      )}
    </Box>
  );
};

export default MainLayout;
